import * as THREE from 'three';
import BaseMesh from '../BaseMesh.js';

// https://blog.csdn.net/allenjiao/article/details/84320386
const assignUVs = (geometry) => {
  geometry.computeBoundingBox();
  const max = geometry.boundingBox.max;
  const min = geometry.boundingBox.min;
  const offset = new THREE.Vector2(0 - min.x, 0 - min.y);
  const range = new THREE.Vector2(max.x - min.x, max.y - min.y);
  const faces = geometry.faces;
  geometry.faceVertexUvs[0] = [];
  for (let i = 0; i < faces.length; i++) {
    const v1 = geometry.vertices[faces[i].a];
    const v2 = geometry.vertices[faces[i].b];
    const v3 = geometry.vertices[faces[i].c];
    geometry.faceVertexUvs[0].push([
      new THREE.Vector2((v1.x + offset.x) / range.x, (v1.y + offset.y) / range.y),
      new THREE.Vector2((v2.x + offset.x) / range.x, (v2.y + offset.y) / range.y),
      new THREE.Vector2((v3.x + offset.x) / range.x, (v3.y + offset.y) / range.y)
    ]);
  }
  geometry.uvsNeedUpdate = true;
};

const getSize = (props) => {
  const { points } = props;
  let minX = Number.MAX_SAFE_INTEGER;
  let maxX = -Number.MAX_SAFE_INTEGER;
  let minY = Number.MAX_SAFE_INTEGER;
  let maxY = -Number.MAX_SAFE_INTEGER;
  points.forEach((point) => {
    minX = Math.min(minX, point[0]);
    maxX = Math.max(maxX, point[0]);
    minY = Math.min(minY, point[1]);
    maxY = Math.max(maxY, point[1]);
  });
  const width = maxX - minX;
  const height = maxY - minY;
  return { width, height };
};

// 点的转换：世界坐标系转为工件坐标系
const pointsWorldToLocal = (pointsWorld) => {
  let minX = Number.MAX_SAFE_INTEGER;
  let minY = Number.MAX_SAFE_INTEGER;
  pointsWorld.forEach((point) => {
    minX = Math.min(minX, point[0]);
    minY = Math.min(minY, point[1]);
  });
  const pointsLocal = JSON.parse(JSON.stringify(pointsWorld));
  pointsLocal.forEach((point) => {
    point[0] -= minX;
    point[1] -= minY;
  });
  return pointsLocal;
};

// 点的转换：three 坐标系转为 canvas 坐标系; 两个坐标系成镜像关系
const pointsThreeToCanvas = (pointsThree) => {
  let minY = Number.MAX_SAFE_INTEGER;
  let maxY = -Number.MAX_SAFE_INTEGER;
  pointsThree.forEach((point) => {
    minY = Math.min(minY, point[1]);
    maxY = Math.max(maxY, point[1]);
  });
  const height = maxY - minY;
  const pointsCanvas = JSON.parse(JSON.stringify(pointsThree));
  pointsCanvas.forEach((point) => {
    point[1] = height - point[1];
  });
  return pointsCanvas;
};

const getFont = (size) => {
  return `normal ${size}px Arial`;
};

const getTextSize = (props) => {
  const { text, size } = props.roiText;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = getFont(size);
  const width = ctx.measureText(text).width;
  const height = ctx.measureText('M').width;
  return { width, height };
};

const pointsToVertices = (points) => {
  // 提取数据，封闭图形
  const vertices = [];
  points.forEach((e) => {
    const [x, y, z] = e;
    vertices.push(new THREE.Vector3(x, y, z));
  });
  const [x, y, z] = points[0];
  vertices.push(new THREE.Vector3(x, y, z));
  return vertices;
};

class Index extends BaseMesh {
  constructor(data) {
    super(data);
    this._drawAll();
  }

  _drawAll() {
    // 拆解数据
    const { border, polygon, roiText, points } = this.props;
    const { width, height } = getSize(this.props);
    const vertices = pointsToVertices(points);

    // init canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // ctx.globalCompositeOperation = "copy";

    // 绘制 polygon 背景色
    ctx.fillStyle = `${polygon.color}`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // draw border
    // 世界坐标，转为工件坐标
    const pointsLocal = pointsWorldToLocal(points);
    const pointsCanvas = pointsThreeToCanvas(pointsLocal);
    ctx.strokeStyle = border.color;
    ctx.lineWidth = border.width * 2; // 有一半绘制在 canvas 外面，看不见，因此需要乘以 2
    // ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(pointsCanvas[0][0], pointsCanvas[0][1]);
    for (let i = 1; i < pointsCanvas.length; i++) {
      ctx.lineTo(pointsCanvas[i][0], pointsCanvas[i][1]);
    }
    ctx.closePath();
    ctx.stroke();
    // draw text
    ctx.fillStyle = roiText.color;
    ctx.font = getFont(roiText.size);
    ctx.textBaseline = 'middle';
    const { width: textWidth } = getTextSize(this.props);
    ctx.fillText(roiText.text, (width - textWidth) / 2, height / 2);

    // 生成 canvasTexture
    const canvasTexture = new THREE.CanvasTexture(canvas);
    canvasTexture.needsUpdate = true;

    // 设置 this.geometry，this.material
    this.geometry = new THREE.ShapeGeometry(new THREE.Shape(vertices));
    assignUVs(this.geometry);
    const { vertices: _vertices } = this.geometry;

    function average(vertices) {
      let xTotal = 0;
      let yTotal = 0;
      let zTotal = 0;
      const length = vertices.length;
      vertices.map((value) => {
        xTotal += value.x;
        yTotal += value.y;
        zTotal += value.z;
      });
      return {
        x: xTotal / length,
        y: yTotal / length,
        z: zTotal / length
      };
    }
    const { x, y, z } = average(_vertices);
    if (this.label) {
      this.label.position.set(x, y, z); // 中心点对齐坐标点
    }
    this.material = new THREE.MeshBasicMaterial({
      map: canvasTexture,
      transparent: true,
      depthTest: false,
    });
  }

  updateProps(key, value) {
    super.updateProps(key, value);
    switch (key) {
      case 'border':
      case 'polygon':
      case 'points':
      case 'roiText':
        this._drawAll();
        break;
    }
  }
}

export default Index;
